import { onboardingTaskProgress } from "../../shared/onboarding-progress";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { useNavigate } from "react-router-dom";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CheckCircle2, Circle, Clock, MessageSquare, Send,
  Sparkles, CalendarDays, ChevronDown, ChevronUp,
  FileText, ExternalLink, Paperclip, Save, ArrowLeft, Inbox, Plus, UserPlus, ArrowRight,
} from "lucide-react";
import {
  OnboardingSession, OnboardingTask, HRDocument,
  OnboardingMessageThread, OnboardingMessage,
  apiGetMyOnboarding, apiUpdateOnboardingTask,
  apiGetHRDocument,
  apiGetOnboardingThreads, apiGetOnboardingThread, apiReplyOnboardingThread,
  apiComposeOnboardingMessage,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeaderSkeleton, StatCardsSkeleton, CardGridSkeleton, ListSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  formatOnboardingDate,
  getOnboardingDueState,
  getOnboardingRemainingDays,
} from "@/lib/onboarding-format";

const categoryConfig: Record<string, { color: string; bg: string }> = {
  paperwork: { color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
  training: { color: "text-green-600", bg: "bg-green-50 border-green-200" },
  setup: { color: "text-purple-600", bg: "bg-purple-50 border-purple-200" },
  introduction: { color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  compliance: { color: "text-red-600", bg: "bg-red-50 border-red-200" },
  custom: { color: "text-gray-600", bg: "bg-gray-50 border-gray-200" },
};

const taskIsOpen = (task: OnboardingTask) => task.status !== "completed" && task.status !== "skipped";

function taskDueTime(task: OnboardingTask) {
  return task.dueDate ? new Date(task.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
}

export default function MyOnboarding() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState<OnboardingSession | null>(null);
  const [loading, setLoading] = useState(true);
  // Messages / threads
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [chatView, setChatView] = useState<"threads" | "thread" | "compose">("threads");
  const [threads, setThreads] = useState<OnboardingMessageThread[]>([]);
  const [activeThread, setActiveThread] = useState<OnboardingMessageThread | null>(null);
  const [threadMessages, setThreadMessages] = useState<OnboardingMessage[]>([]);
  const [threadInput, setThreadInput] = useState("");
  const [threadSending, setThreadSending] = useState(false);
  // Compose state
  const [composeTarget, setComposeTarget] = useState<"manager" | "buddy" | "hr">("manager");
  const [composeContent, setComposeContent] = useState("");
  const [composeSending, setComposeSending] = useState(false);

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(["paperwork", "training", "setup", "introduction", "compliance", "custom"]));
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [animProgress, setAnimProgress] = useState(0);

  // Task action sheet
  const [taskSheetOpen, setTaskSheetOpen] = useState(false);
  const [activeTask, setActiveTask] = useState<OnboardingTask | null>(null);
  const [taskNotes, setTaskNotes] = useState("");
  const [taskStatus, setTaskStatus] = useState<string>("pending");
  const [taskSaving, setTaskSaving] = useState(false);
  const [myDocuments, setMyDocuments] = useState<HRDocument[]>([]);
  const formattedDate = (value: string | Date | null | undefined) => formatOnboardingDate(value, i18n.resolvedLanguage || i18n.language);

  const loadSession = useCallback(async () => {
    try {
      setLoading(true);
      const s = await apiGetMyOnboarding();
      setSession(s ? { ...s, progress: onboardingTaskProgress(s.tasks) } : null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSession(); }, [loadSession]);

  useEffect(() => {
    if (!session) return;
    setAnimProgress(onboardingTaskProgress(session.tasks));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.progress, session?.tasks]);

  const loadThreads = async () => {
    try {
      const t = await apiGetOnboardingThreads();
      setThreads(t);
    } catch { /* ignore */ }
  };

  const openThread = async (thread: OnboardingMessageThread) => {
    setActiveThread(thread);
    setChatView("thread");
    try {
      const msgs = await apiGetOnboardingThread(thread.threadId);
      setThreadMessages(msgs);
    } catch { /* ignore */ }
  };

  const handleSendThreadReply = async () => {
    if (!activeThread || !threadInput.trim() || threadSending) return;
    const content = threadInput.trim();
    setThreadInput("");
    setThreadSending(true);
    try {
      const msg = await apiReplyOnboardingThread(activeThread.threadId, content);
      setThreadMessages((prev) => [...prev, msg]);
    } catch { /* ignore */ }
    finally { setThreadSending(false); }
  };

  const handleCompose = async () => {
    if (!composeContent.trim() || composeSending) return;
    setComposeSending(true);
    try {
      const { threadId } = await apiComposeOnboardingMessage(composeTarget, composeContent.trim());
      setComposeContent("");
      setComposeTarget("manager");
      // Reload threads and open the new one
      const fresh = await apiGetOnboardingThreads();
      setThreads(fresh);
      const newThread = fresh.find((t) => t.threadId === threadId);
      if (newThread) {
        setActiveThread(newThread);
        const msgs = await apiGetOnboardingThread(threadId);
        setThreadMessages(msgs);
        setChatView("thread");
      } else {
        setChatView("threads");
      }
    } catch { /* ignore */ }
    finally { setComposeSending(false); }
  };

  const handleToggleTask = async (task: OnboardingTask) => {
    if (!session || taskSaving || task.canEdit === false) return;
    setTaskSaving(true);
    const newStatus = task.status === "completed" ? "pending" : "completed";
    try {
      const result = await apiUpdateOnboardingTask(session.id, task.id, { status: newStatus });
      setSession({
        ...session,
        progress: result.progress,
        status: result.sessionStatus,
        tasks: session.tasks.map((currentTask) => (currentTask.id === task.id
          ? { ...currentTask, status: result.status, completedAt: result.completedAt, notes: result.notes, documentIds: result.documentIds }
          : currentTask)),
      });
    } catch (e: any) {
      const description = e?.code === "ONBOARDING_REQUIRED_DOCUMENT_MISSING"
        ? t("onboarding.requiredDocumentMissing")
        : e?.code === "ONBOARDING_REQUIRED_DOCUMENT_INCOMPLETE"
          ? t("onboarding.requiredDocumentIncomplete")
          : e?.code === "ONBOARDING_TASK_FORBIDDEN"
            ? t("onboarding.taskUpdateForbidden")
          : e?.message;
      toast({ title: t("common.error"), description, variant: "destructive" });
    } finally { setTaskSaving(false); }
  };

  const openTaskSheet = async (task: OnboardingTask) => {
    setActiveTask(task);
    setTaskNotes(task.notes || "");
    setTaskStatus(task.status);
    setMyDocuments([]);
    setTaskSheetOpen(true);
    if (task.documentIds?.length) {
      try {
        const docs = await Promise.all(task.documentIds.map((documentId) => apiGetHRDocument(documentId)));
        setMyDocuments(docs);
      } catch { setMyDocuments([]); }
    }
  };

  const handleSaveTask = async () => {
    if (!session || !activeTask || taskSaving || activeTask.canEdit === false) return;
    setTaskSaving(true);
    try {
      const result = await apiUpdateOnboardingTask(session.id, activeTask.id, {
        status: taskStatus,
        notes: taskNotes || undefined,
      });
      setSession({
        ...session,
        progress: result.progress,
        status: result.sessionStatus,
        tasks: session.tasks.map((currentTask) => currentTask.id === activeTask.id
          ? { ...currentTask, status: result.status, completedAt: result.completedAt, notes: result.notes, documentIds: result.documentIds }
          : currentTask),
      });
      setTaskSheetOpen(false);
    } catch (e: any) {
      const description = e?.code === "ONBOARDING_REQUIRED_DOCUMENT_MISSING"
        ? t("onboarding.requiredDocumentMissing")
        : e?.code === "ONBOARDING_REQUIRED_DOCUMENT_INCOMPLETE"
          ? t("onboarding.requiredDocumentIncomplete")
          : e?.code === "ONBOARDING_TASK_FORBIDDEN"
            ? t("onboarding.taskUpdateForbidden")
          : e?.message;
      toast({ title: t("common.error"), description, variant: "destructive" });
    } finally {
      setTaskSaving(false);
    }
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const getCategoryLabel = (cat: string) => {
    const labels: Record<string, string> = {
      paperwork: t("onboarding.categoryPaperwork", "Paperwork"),
      training: t("onboarding.categoryTraining", "Training"),
      setup: t("onboarding.categorySetup", "Setup"),
      introduction: t("onboarding.categoryIntroduction", "Introductions"),
      compliance: t("onboarding.categoryCompliance", "Compliance"),
      custom: t("onboarding.categoryCustom", "Custom"),
    };
    return labels[cat] || cat;
  };

  const getAssigneeLabel = (assigneeType: string) => {
    const labels: Record<string, string> = {
      new_hire: t("onboarding.assigneeNewHire", "You"),
      hr: t("onboarding.assigneeHr", "HR"),
      manager: t("onboarding.assigneeManager", "Manager"),
      buddy: t("onboarding.assigneeBuddy", "Buddy"),
    };
    return labels[assigneeType] || assigneeType.replace("_", " ");
  };

  const openOnboardingAssistant = () => {
    if (!session) return;
    const openTasks = session.tasks
      .filter(taskIsOpen)
      .sort((a, b) => taskDueTime(a) - taskDueTime(b) || a.id - b.id);
    const nextTask = openTasks[0] || null;

    window.dispatchEvent(new CustomEvent("sara:open", {
      detail: {
        context: "onboarding",
        onboarding: {
          role: session.employeeRole || undefined,
          department: session.employeeDepartment || undefined,
          buddyName: session.buddyName || undefined,
          progress: session.progress,
          totalTasks: session.tasks.length,
          completedTasks: session.tasks.filter((task) => task.status === "completed").length,
          pendingTasks: openTasks.length,
          nextTaskTitle: nextTask?.title,
          nextTaskDescription: nextTask?.description || undefined,
        },
      },
    }));
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full">
          <PageHeaderSkeleton />
          <StatCardsSkeleton count={4} />
          <CardGridSkeleton count={3} />
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <ListSkeleton rows={5} />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!session) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <div className="h-20 w-20 mx-auto rounded-2xl bg-secondary flex items-center justify-center mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{t("onboarding.noActiveSession")}</h2>
          <p className="text-muted-foreground">{t("onboarding.noActiveSessionDesc")}</p>
        </div>
      </AppLayout>
    );
  }

  const totalTasks = session.tasks.length;
  const completedTasks = session.tasks.filter((t) => t.status === "completed").length;
  const overdueTasks = session.tasks.filter((task) => taskIsOpen(task) && getOnboardingDueState(task.dueDate) === "overdue").length;
  const daysLeft = getOnboardingRemainingDays(session.expectedEndDate);
  const openTasks = session.tasks
    .filter(taskIsOpen)
    .sort((a, b) => {
      if (a.status === "in_progress" && b.status !== "in_progress") return -1;
      if (b.status === "in_progress" && a.status !== "in_progress") return 1;
      return taskDueTime(a) - taskDueTime(b) || a.id - b.id;
    });
  const nextTask = openTasks[0] || null;

  const categories = [...new Set(session.tasks.map((t) => t.category))];

  return (
    <AppLayout>
      <div className="space-y-6 w-full">

        {/* Page header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <UserPlus className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("onboarding.yourOnboarding")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("onboarding.welcomeMessage")}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => { setMessagesOpen(true); loadThreads(); }}
            >
              <MessageSquare className="h-4 w-4" />
              {t("onboarding.messages")}
            </Button>
            <Button
              className="gap-2 gradient-brand text-primary-foreground"
              onClick={openOnboardingAssistant}
            >
              <Sparkles className="h-4 w-4" />
              {t("onboarding.aiAssistant")}
            </Button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{animProgress}%</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding.progress")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{completedTasks}<span className="text-sm font-normal text-muted-foreground">/{totalTasks}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding.tasksCompleted")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <CalendarDays className="h-4 w-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{daysLeft !== null ? daysLeft : "—"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding.daysLeft")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                <Clock className="h-4 w-4 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{overdueTasks}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding.overdue")}</p>
              </div>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-border/60 bg-muted/20">
            <Progress value={animProgress} className="h-1.5" />
          </div>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {session.templateName && (
            <Card className="border border-border/60 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t("onboarding.template")}</p>
                  <p className="text-sm font-medium truncate">{session.templateName}</p>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border border-border/60 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <CalendarDays className="h-4 w-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t("onboarding.startDate")}</p>
                <p className="text-sm font-medium">{formattedDate(session.startDate)}</p>
              </div>
            </CardContent>
          </Card>
          {session.buddyName && (
            <Card className="border border-border/60 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/employees/${session.buddyId}`)}>
              <CardContent className="p-4 flex items-center gap-3">
                <EmployeeAvatar
                  avatarUrl={session.buddyAvatarUrl}
                  avatarColor={session.buddyAvatarColor || "from-blue-400 to-indigo-500"}
                  initials={session.buddyName.split(" ").filter(Boolean).map(n => n[0]).join("").toUpperCase()}
                  size="sm"
                  className="h-10 w-10 shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t("onboarding.yourBuddy")}</p>
                  <p className="text-sm font-medium truncate">{session.buddyName}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Next action */}
        {nextTask ? (
          <Card className="border border-blue-200 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-900 shadow-sm">
            <CardContent className="p-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                  <ArrowRight className="h-4 w-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase text-blue-700 dark:text-blue-300">{t("onboarding.nextStep", "Next step")}</p>
                  <h2 className="text-base font-semibold text-foreground mt-0.5">{nextTask.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {nextTask.description || t("onboarding.nextStepDesc", "Open this task, review the details, and update the status when you are done.")}
                  </p>
                </div>
              </div>
              <Button className="gap-2 shrink-0" onClick={() => openTaskSheet(nextTask)}>
                {t("onboarding.openTask", "Open task")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ) : totalTasks > 0 ? (
          <Card className="border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-900 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold">{t("onboarding.allTasksDone", "All onboarding tasks are complete")}</p>
                <p className="text-xs text-muted-foreground">{t("onboarding.allTasksDoneDesc", "Your onboarding checklist is complete. HR will review anything that needs approval.")}</p>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Tasks by category */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t("onboarding.yourTasks")}
            </h2>
            {totalTasks > 0 && (
              <span className="text-xs text-muted-foreground">
                {t("onboarding.remainingTasks", { count: openTasks.length, defaultValue: "{{count}} remaining" })}
              </span>
            )}
          </div>
          {totalTasks === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-muted/20 px-5 py-8 text-center">
              <Inbox className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <h3 className="text-sm font-semibold text-foreground">{t("onboarding.noTasksTitle", "No onboarding tasks assigned yet")}</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl mx-auto">
                {t("onboarding.noTasksDesc", "Your onboarding session is active, but no tasks were created for it yet. Contact HR so they can add the first steps to your checklist.")}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 gap-2"
                onClick={() => { setMessagesOpen(true); setChatView("compose"); setComposeTarget("hr"); loadThreads(); }}
              >
                <MessageSquare className="h-4 w-4" />
                {t("onboarding.messageHr", "Message HR")}
              </Button>
            </div>
          ) : categories.map((cat) => {
            const catTasks = session.tasks.filter((t) => t.category === cat);
            const catCompleted = catTasks.filter((t) => t.status === "completed").length;
            const cfg = categoryConfig[cat] || categoryConfig.custom;
            const isExpanded = expandedCategories.has(cat);

            return (
              <Card key={cat} className="border border-border/60 shadow-sm overflow-hidden">
                <button className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors" onClick={() => toggleCategory(cat)}>
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
                    {catCompleted === catTasks.length
                      ? <CheckCircle2 className={`h-4 w-4 ${cfg.color}`} />
                      : <Circle className={`h-4 w-4 ${cfg.color}`} />}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <span className="font-medium text-sm">{getCategoryLabel(cat)}</span>
                    <span className="text-xs text-muted-foreground ml-2">{catCompleted}/{catTasks.length}</span>
                  </div>
                  <Progress value={onboardingTaskProgress(catTasks)} className="w-20 h-1.5" />
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                </button>

                {isExpanded && (
                  <div className="border-t border-border/60 divide-y divide-border/40">
                    {catTasks.map((task) => {
                      const dueState = taskIsOpen(task) ? getOnboardingDueState(task.dueDate) : null;
                      const isOverdue = dueState === "overdue";
                      const canEdit = task.canEdit !== false;
                      return (
                        <div
                          key={task.id}
                          className={`flex items-start gap-3 px-4 py-3 hover:bg-muted/20 transition-colors cursor-pointer ${task.status === "completed" ? "opacity-60" : ""}`}
                          onClick={() => openTaskSheet(task)}
                        >
                          <button
                            type="button"
                            disabled={!canEdit || taskSaving}
                            aria-label={canEdit ? t("onboarding.taskStatus") : t("onboarding.readOnlyTask")}
                            className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${
                              task.status === "completed"
                                ? "bg-emerald-500 border-emerald-500 text-white"
                                : canEdit ? "border-border hover:border-emerald-400" : "border-border cursor-not-allowed opacity-60"
                            }`}
                            onClick={(e) => { e.stopPropagation(); handleToggleTask(task); }}
                          >
                            {task.status === "completed" && <CheckCircle2 className="h-3 w-3" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${task.status === "completed" ? "line-through text-muted-foreground" : ""}`}>{task.title}</p>
                            {task.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{task.description}</p>}
                            <div className="flex gap-3 mt-1.5 flex-wrap">
                              {task.dueDate && (
                                <span className={`text-xs flex items-center gap-1 ${isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                                  <Clock className="h-3 w-3" />{formattedDate(task.dueDate)}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {task.assigneeName
                                  ? `${task.assigneeName} (${getAssigneeLabel(task.assigneeType)})`
                                  : getAssigneeLabel(task.assigneeType)}
                              </span>
                              {(task.documentIds?.length || 0) > 0 && (
                                <span className="text-xs text-blue-600 flex items-center gap-1">
                                  <Paperclip className="h-3 w-3" />
                                  {t("onboarding.documentsCount", { count: task.documentIds.length, defaultValue: "{{count}} document(s)" })}
                                </span>
                              )}
                            </div>
                          </div>
                          {isOverdue && <Badge variant="destructive" className="text-[10px] shrink-0">{t("onboarding.overdue")}</Badge>}
                          {dueState === "today" && <Badge variant="outline" className="text-[10px] shrink-0">{t("onboarding.dueToday")}</Badge>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Task Action Dialog */}
      <Dialog open={taskSheetOpen} onOpenChange={setTaskSheetOpen}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>{activeTask?.title}</DialogTitle>
          </DialogHeader>
          {activeTask && (
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {activeTask.description && (
                <div className="p-3 rounded-lg bg-secondary/50 text-sm">{activeTask.description}</div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{t("onboarding.category")}</span>
                  <Badge className={`ml-2 ${categoryConfig[activeTask.category]?.bg || ""} ${categoryConfig[activeTask.category]?.color || ""}`}>
                    {getCategoryLabel(activeTask.category)}
                  </Badge>
                </div>
                {activeTask.dueDate && (
                  <div>
                    <span className="text-muted-foreground">{t("onboarding.due")}: </span>
                    <span className="font-medium">{formattedDate(activeTask.dueDate)}</span>
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-2 block">{t("onboarding.taskStatus")}</Label>
                {activeTask.canEdit !== false ? (
                  <Select value={taskStatus} onValueChange={setTaskStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">{t("onboarding.statusPending")}</SelectItem>
                      <SelectItem value="in_progress">{t("onboarding.statusInProgress")}</SelectItem>
                      <SelectItem value="completed">{t("onboarding.statusCompleted")}</SelectItem>
                      <SelectItem value="skipped">{t("onboarding.statusSkipped")}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <p>{t(`onboarding.status${taskStatus === "in_progress" ? "InProgress" : taskStatus.charAt(0).toUpperCase() + taskStatus.slice(1)}`)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("onboarding.readOnlyTask")}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-2 block">{t("onboarding.notes")}</Label>
                <Textarea
                  placeholder={t("onboarding.taskNotesPlaceholder")}
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  className="min-h-[100px]"
                  disabled={activeTask.canEdit === false}
                />
              </div>

              {(activeTask.taskType === "document_upload" || activeTask.documentIds.length > 0) && <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <FileText className="h-4 w-4" />{t("onboarding.linkedDocuments")}
                </Label>
                {myDocuments.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {myDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 p-2.5 rounded-lg border">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{doc.documentName}</p>
                          <p className="text-xs text-muted-foreground">{t(`documents.status.${doc.status}`, doc.status)}</p>
                        </div>
                        {doc.id && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                            aria-label={t("onboarding.openLinkedDocument")}
                            onClick={() => navigate(`/my-documents?openDocument=${doc.id}`)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("onboarding.noLinkedDocuments")}</p>
                )}
              </div>}

              {activeTask.canEdit !== false && (
                <Button onClick={handleSaveTask} disabled={taskSaving} className="w-full">
                  <Save className="h-4 w-4 mr-2" />{taskSaving ? t("common.saving") : t("common.save")}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Messages Dialog (threads with manager/buddy/HR — no AI chat here, use Sara instead) */}
      <Dialog open={messagesOpen} onOpenChange={(open) => { setMessagesOpen(open); if (!open) { setChatView("threads"); setActiveThread(null); setComposeContent(""); } }}>
        <DialogContent className="sm:max-w-lg h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
          <div className="px-6 pt-6 pb-3 border-b">
            {chatView === "thread" ? (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setChatView("threads"); setActiveThread(null); }}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{activeThread?.otherName}</p>
                  <p className="text-xs text-muted-foreground">{t("onboarding.onboardingConversation")}</p>
                </div>
              </div>
            ) : chatView === "compose" ? (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setChatView("threads"); setComposeContent(""); }}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <p className="text-sm font-semibold">{t("onboarding.newMessage")}</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <Inbox className="h-4 w-4" />{t("onboarding.messages")}
                    {threads.reduce((s, th) => s + th.unreadCount, 0) > 0 && (
                      <Badge className="h-5 min-w-[20px] px-1 text-xs bg-red-500 text-white">{threads.reduce((s, th) => s + th.unreadCount, 0)}</Badge>
                    )}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => setChatView("compose")}
                  >
                    <Plus className="h-3 w-3" />
                    {t("onboarding.newConversation", "New")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Compose new thread */}
          {chatView === "compose" && (
            <div className="flex flex-col flex-1 px-6 py-5 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="compose-target">{t("onboarding.sendTo")}</Label>
                <Select value={composeTarget} onValueChange={(v) => setComposeTarget(v as "manager" | "buddy" | "hr")}>
                  <SelectTrigger id="compose-target">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">{t("onboarding.lineManager")}</SelectItem>
                    <SelectItem value="buddy">{t("onboarding.onboardingBuddy")}</SelectItem>
                    <SelectItem value="hr">{t("onboarding.hrTeam")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 flex-1">
                <Label htmlFor="compose-content">{t("onboarding.messageLabel")}</Label>
                <Textarea
                  id="compose-content"
                  placeholder={t("onboarding.typeYourMessage")}
                  value={composeContent}
                  onChange={(e) => setComposeContent(e.target.value)}
                  rows={6}
                  className="resize-none h-40"
                  onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleCompose(); }}
                />
                <p className="text-[11px] text-muted-foreground">{t("onboarding.ctrlEnterSend")}</p>
              </div>
              <Button
                onClick={handleCompose}
                disabled={!composeContent.trim() || composeSending}
                className="w-full gap-2"
              >
                <Send className="h-4 w-4" />
                {composeSending ? t("onboarding.sending") : t("onboarding.sendMessage")}
              </Button>
            </div>
          )}

          {/* Thread list */}
          {chatView === "threads" && (
            <ScrollArea className="flex-1">
              {threads.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <Inbox className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground mb-4">{t("onboarding.noMessages")}</p>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setChatView("compose")}>
                    <Plus className="h-3.5 w-3.5" />
                    {t("onboarding.startConversation", "Start a conversation")}
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {threads.map((thread) => (
                    <button
                      key={thread.threadId}
                      className="w-full px-6 py-4 text-left hover:bg-muted/30 transition-colors flex items-start gap-3"
                      onClick={() => openThread(thread)}
                    >
                      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5">
                        {thread.otherName?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm truncate ${thread.unreadCount > 0 ? "font-semibold" : "font-medium"}`}>{thread.otherName}</p>
                          <span className="text-xs text-muted-foreground shrink-0 ml-2">{formattedDate(thread.lastAt)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{thread.lastMessage}</p>
                      </div>
                      {thread.unreadCount > 0 && (
                        <Badge className="bg-red-500 text-white h-5 min-w-[20px] px-1 text-xs shrink-0">{thread.unreadCount}</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}

          {/* Thread detail */}
          {chatView === "thread" && (
            <>
              <ScrollArea className="flex-1 px-6 py-4">
                <div className="space-y-3">
                  {threadMessages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.senderId === Number(user?.id) ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[85%] space-y-1">
                        {msg.senderId !== Number(user?.id) && (
                          <p className="text-xs text-muted-foreground ml-1">{msg.senderName}</p>
                        )}
                        <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                          msg.senderId === Number(user?.id)
                            ? "bg-primary text-primary-foreground rounded-br-md"
                            : "bg-secondary rounded-bl-md"
                        }`}>
                          {msg.content}
                        </div>
                        <p className="text-[10px] text-muted-foreground ml-1">
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>
              <div className="border-t px-6 py-4">
                <form onSubmit={(e) => { e.preventDefault(); handleSendThreadReply(); }} className="flex gap-2">
                  <Input placeholder={t("onboarding.typeReply")} value={threadInput} onChange={(e) => setThreadInput(e.target.value)} disabled={threadSending} className="flex-1" />
                  <Button type="submit" size="icon" disabled={!threadInput.trim() || threadSending}><Send className="h-4 w-4" /></Button>
                </form>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
